import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Vehicle, LocationInfo } from '../../types';
import { formatPrice, calculateHours, calculateRentalPrice } from '../../utils/dateUtils';
import { productOptionApi, ProductOption } from '../../api/productOptionApi';
import { useBookingStore } from '../../stores/bookingStore';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../api/client';
import {
  localizeCarClass,
  localizeFuel,
  localizeLocation,
  localizeOption,
  localizeVehicleModel,
} from '../../utils/localization';

interface UserCoupon {
  user_coupon_id: number;
  coupon_id: number;
  code: string;
  name: string;
  description: string | null;
  coupon_type: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  max_discount_amount: number | null;
  min_order_amount: number | null;
  issued_at: string;
  valid_from: string;
  valid_until: string;
  is_used: boolean;
  used_at: string | null;
  issue_source: string;
}

interface PaymentSummaryProps {
  vehicle: Vehicle;
  startDateTime: Date;
  endDateTime: Date;
  userName: string;
  userPhone: string;
  pickupLocationInfo?: LocationInfo;
  returnLocationInfo?: LocationInfo;
  selectedInsuranceId?: number | null;
  selectedEquipmentIds?: number[];
  showCouponSelector?: boolean;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  vehicle,
  startDateTime,
  endDateTime,
  userName,
  userPhone,
  pickupLocationInfo,
  returnLocationInfo,
  selectedInsuranceId,
  selectedEquipmentIds = [],
  showCouponSelector = false,
}) => {
  const { t, i18n } = useTranslation();
  const [deliveryOptions, setDeliveryOptions] = useState<ProductOption[]>([]);
  const [insuranceOptions, setInsuranceOptions] = useState<ProductOption[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<ProductOption[]>([]);
  const [baseAmount, setBaseAmount] = useState<number>(0);
  const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(true);

  // 쿠폰 관련 state
  const { user, isAuthenticated } = useAuthStore();
  const { setSelectedCoupon: setBookingCoupon } = useBookingStore();
  const [availableCoupons, setAvailableCoupons] = useState<UserCoupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<UserCoupon | null>(null);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);

  const totalHours = calculateHours(startDateTime, endDateTime);
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  const isEnglish = i18n.language.startsWith('en');
  const modelLabels: Record<string, string> = {
    베뉴: 'Venue',
    '제네시스 G80': 'Genesis G80',
    G80: 'Genesis G80',
    '테슬라Model Y': 'Tesla Model Y',
    '테슬라 Model Y': 'Tesla Model Y',
    'Model Y': 'Model Y',
    '볼트 EV': 'Bolt EV',
    '볼트(EV)': 'Bolt EV',
    아반떼: 'Avante',
    '아반떼 AD': 'Avante AD',
    '아반떼 CN7': 'Avante CN7',
    '아반떼 CN7 FL': 'Avante CN7 FL',
    K5: 'K5',
    '3세대 K5': '3rd Gen K5',
    소나타: 'Sonata',
    '소나타 DN8': 'Sonata DN8',
    쏘나타: 'Sonata',
    '쏘나타 디 엣지': 'Sonata The Edge',
    그랜저: 'Grandeur',
    그랜져: 'Grandeur',
    '그랜저 GN7': 'Grandeur GN7',
    '그랜져 GN7': 'Grandeur GN7',
    캐스퍼: 'Casper',
    레이: 'Ray',
    토레스: 'Torres',
    스포티지: 'Sportage',
    카니발: 'Carnival',
    '4세대 카니발': '4th Gen Carnival',
    '4세대카니발': '4th Gen Carnival',
    '카니발 리무진 7인승': 'Carnival Limousine 7-Seater',
    '카니발 리무진 4세대 7인승': '4th Gen Carnival Limousine 7-Seater',
    '4세대 카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발리무진': '4th Gen Carnival Limousine',
    '카니발 9인승': 'Carnival 9-Seater',
    'All New 카니발 9인승': 'All New Carnival 9-Seater',
    'All New 카니발 4세대 9인승': 'All New 4th Gen Carnival 9-Seater',
    '카니발 리무진': 'Carnival Limousine',
    'All New 카니발 리무진': 'All New Carnival Limousine',
    그랜드스타렉스: 'Grand Starex',
    스타리아: 'Staria',
    '스타리아 9인승': 'Staria 9-Seater',
    '스타리아 11인승': 'Staria 11-Seater',
    스타렉스: 'Starex',
    쏠라티: 'Solati',
    셀토스: 'Seltos',
    쏘렌토: 'Sorento',
    '4세대 쏘렌토 FL': '4th Gen Sorento FL',
    '소렌토MQ4 FL': 'Sorento MQ4 FL',
    싼타페: 'Santa Fe',
    투싼: 'Tucson',
    팰리세이드: 'Palisade',
    '디 올뉴 펠리세이드': 'The All-New Palisade',
  };
  const carClassLabels: Record<string, string> = {
    경차: 'Compact',
    소형: 'Economy',
    준중형: 'Compact Sedan',
    중형: 'Midsize',
    대형: 'Full-size',
    수입: 'Imported',
    RV: 'RV',
    SUV: 'SUV',
  };
  const fuelLabels: Record<string, string> = {
    휘발유: 'Gasoline',
    LPG: 'LPG',
    경유: 'Diesel',
    전기: 'Electric',
    하이브리드: 'Hybrid',
  };
  const getModelLabel = (modelName?: string) => {
    return localizeVehicleModel(modelName, i18n.language);
  };
  const getCarClassLabel = (carClass?: string) => {
    return localizeCarClass(carClass, i18n.language);
  };
  const getFuelLabel = (fuel?: string) => {
    return localizeFuel(fuel, i18n.language);
  };
  const vehicleMeta = [
    vehicle.capacity || vehicle.seating_capacity ? `${vehicle.capacity || vehicle.seating_capacity}${t('vehicleList.seats')}` : '',
    getFuelLabel(vehicle.fuel_type || vehicle.fuel),
    getCarClassLabel(vehicle.car_class || vehicle.vehicle_type),
  ].filter(Boolean).join(' · ');
  const getLocationLabel = (location?: string) => localizeLocation(location, i18n.language);
  const getOptionLabel = (option?: string) => localizeOption(option, i18n.language);
  const formatDateTime = (date: Date) => date.toLocaleString(i18n.language.startsWith('en') ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const formatDuration = () => `${days > 0 ? `${days}${t('vehicleList.day')} ` : ''}${remainingHours}${t('vehicleList.hour')}`;

  // 본점 주소
  const mainBranchAddress = '부산 부산진구 전포동 674-4';

  // Calculate rental price on frontend
  useEffect(() => {
    // Get weekday and weekend rates from vehicle
    const weekdayRate = vehicle.weekday_rate || vehicle.price_per_day || 0;
    const weekendRate = vehicle.weekend_rate || vehicle.price_per_day || weekdayRate;
    const fiveHourRate = vehicle.five_hour_rate || 0;

    // Calculate rental price
    const rentalPrice = calculateRentalPrice(
      startDateTime,
      endDateTime,
      weekdayRate,
      weekendRate,
      fiveHourRate
    );

    setBaseAmount(rentalPrice);
    setIsLoadingPrice(false);
  }, [vehicle, startDateTime, endDateTime]);

  // Load available coupons
  useEffect(() => {
    let isMounted = true;

    const loadCoupons = async () => {
      console.log('쿠폰 로딩 조건 체크:', { isAuthenticated, userId: user?.id, isMounted });
      if (!isAuthenticated || !user?.id || !isMounted) return;

      setIsLoadingCoupons(true);
      try {
        const response = await apiClient.get(`/users/${user.id}/coupons`, {
          params: { only_available: true }
        });

        if (!isMounted) return;

        console.log('쿠폰 로딩 성공:', response.data.coupons);
        setAvailableCoupons(response.data.coupons || []);
      } catch (error) {
        console.error('Failed to load coupons:', error);
      } finally {
        if (isMounted) {
          setIsLoadingCoupons(false);
        }
      }
    };

    loadCoupons();
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?.id]);

  // Load all product options once (optimized: single API call for each type)
  useEffect(() => {
    let isMounted = true;

    const loadAllOptions = async () => {
      if (!isMounted) return;

      try {
        // Load all option types in parallel
        const [deliveryOpts, insuranceOpts, equipmentOpts] = await Promise.all([
          productOptionApi.getAllProductOptions({ is_active: true, type: 'pickup' }),
          productOptionApi.getAllProductOptions({ is_active: true, type: 'insurance' }),
          productOptionApi.getAllProductOptions({ is_active: true, type: 'equipment' }),
        ]);

        if (!isMounted) return;

        setDeliveryOptions(deliveryOpts);
        setInsuranceOptions(insuranceOpts);
        setEquipmentOptions(equipmentOpts);
      } catch (error) {
        console.error('Failed to load product options:', error);
      }
    };

    loadAllOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter selected insurance from loaded options
  const selectedInsurance = useMemo(() => {
    if (selectedInsuranceId && insuranceOptions.length > 0) {
      return insuranceOptions.find(opt => opt.id === selectedInsuranceId) || null;
    }
    return null;
  }, [selectedInsuranceId, insuranceOptions]);

  // Filter selected equipment from loaded options
  const selectedEquipment = useMemo(() => {
    if (selectedEquipmentIds && selectedEquipmentIds.length > 0 && equipmentOptions.length > 0) {
      return equipmentOptions.filter(opt => selectedEquipmentIds.includes(opt.id));
    }
    return [];
  }, [selectedEquipmentIds, equipmentOptions]);

  // 배달 불가 날짜 API 조회
  const [deliveryBlocked, setDeliveryBlocked] = useState(false);

  useEffect(() => {
    const checkDeliveryBlocked = async () => {
      try {
        const formatDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const response = await apiClient.get('/delivery-blocked-dates/public/check', {
          params: {
            start_date: formatDate(startDateTime),
            end_date: formatDate(endDateTime),
          },
        });
        setDeliveryBlocked((response.data || []).length > 0);
      } catch {
        setDeliveryBlocked(false);
      }
    };

    checkDeliveryBlocked();
  }, [startDateTime, endDateTime]);

  // 배송 불가 조건 확인
  const isDeliveryDisabled = (): boolean => {
    const rentalHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    return rentalHours < 24 || deliveryBlocked;
  };

  // 배송료 계산 (대여/반납 위치가 본점이 아닌 경우)
  const isPickupDelivery = pickupLocationInfo && pickupLocationInfo.address !== mainBranchAddress;
  const isReturnDelivery = returnLocationInfo && returnLocationInfo.address !== mainBranchAddress;

  // 편도/왕복 배송료 찾기
  const onewayOption = deliveryOptions.find(opt => opt.name.includes('편도'));
  const roundtripOption = deliveryOptions.find(opt => opt.name.includes('왕복'));

  const ONEWAY_FEE = onewayOption?.price || 10000;
  const ROUNDTRIP_FEE = roundtripOption?.price || 20000;

  // 둘 다 배송이 필요한 경우 왕복, 하나만 필요한 경우 편도
  // 단, 24시간 미만이거나 배달 불가 기간이면 배송비 0
  let deliveryFee = 0;
  if (!isDeliveryDisabled()) {
    if (isPickupDelivery && isReturnDelivery) {
      deliveryFee = ROUNDTRIP_FEE;
    } else if (isPickupDelivery || isReturnDelivery) {
      deliveryFee = ONEWAY_FEE;
    }
  }

  // 보험료 계산
  const rentalDays = Math.max(1, Math.ceil(totalHours / 24));
  let insuranceFee = 0;
  let insuranceLabel = '';

  if (selectedInsurance) {
    const selectedInsuranceLabel = getOptionLabel(selectedInsurance.name);
    if (selectedInsurance.price_type === 'daily') {
      insuranceFee = rentalDays * selectedInsurance.price;
      insuranceLabel = `${selectedInsuranceLabel} (${rentalDays}${t('vehicleList.day')})`;
    } else if (selectedInsurance.price_type === 'hourly') {
      insuranceFee = totalHours * selectedInsurance.price;
      insuranceLabel = `${selectedInsuranceLabel} (${totalHours}${t('vehicleList.hour')})`;
    } else if (selectedInsurance.price_type === 'onetime') {
      insuranceFee = selectedInsurance.price;
      insuranceLabel = selectedInsuranceLabel;
    }
  }

  // 장비 옵션 금액 계산
  const equipmentFees = selectedEquipment.map(equipment => {
    let fee = 0;
    let label = '';
    const equipmentLabel = getOptionLabel(equipment.name);

    if (equipment.price_type === 'daily') {
      fee = rentalDays * equipment.price;
      label = `${equipmentLabel} (${rentalDays}${t('vehicleList.day')})`;
    } else if (equipment.price_type === 'hourly') {
      fee = totalHours * equipment.price;
      label = `${equipmentLabel} (${totalHours}${t('vehicleList.hour')})`;
    } else if (equipment.price_type === 'onetime') {
      fee = equipment.price;
      label = equipmentLabel;
    }

    return { fee, label, equipment };
  });

  const totalEquipmentFee = equipmentFees.reduce((sum, item) => sum + item.fee, 0);

  // 쿠폰 적용 전 금액
  const amountBeforeDiscount = Math.round(baseAmount + insuranceFee + deliveryFee + totalEquipmentFee);

  console.log('Amount breakdown:', {
    baseAmount,
    insuranceFee,
    deliveryFee,
    totalEquipmentFee,
    amountBeforeDiscount,
  });

  // 쿠폰 할인 금액 계산 (표시용 - 조건 무시)
  const calculateCouponDiscountForDisplay = (coupon: UserCoupon, amount: number): number => {
    console.log('calculateCouponDiscountForDisplay called:', {
      coupon_name: coupon.name,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      amount,
      max_discount_amount: coupon.max_discount_amount,
    });

    let discount = 0;
    if (coupon.discount_type === 'percentage') {
      discount = Math.round(amount * (coupon.discount_value / 100));
      console.log('Percentage discount calculated:', discount);
      // 최대 할인 금액 체크
      if (coupon.max_discount_amount && discount > coupon.max_discount_amount) {
        discount = coupon.max_discount_amount;
        console.log('Capped to max_discount_amount:', discount);
      }
    } else if (coupon.discount_type === 'fixed_amount' || coupon.discount_type === 'fixed') {
      discount = coupon.discount_value;
      console.log('Fixed amount discount:', discount);
    }
    const finalDiscount = Math.min(discount, amount);
    console.log('Final display discount:', finalDiscount);
    return finalDiscount;
  };

  // 쿠폰 할인 금액 계산 (실제 적용용 - 조건 포함)
  const calculateCouponDiscount = (coupon: UserCoupon | null, amount: number): number => {
    if (!coupon) return 0;

    // 최소 주문 금액 체크
    if (coupon.min_order_amount && amount < coupon.min_order_amount) {
      return 0;
    }

    return calculateCouponDiscountForDisplay(coupon, amount);
  };

  const couponDiscount = calculateCouponDiscount(selectedCoupon, amountBeforeDiscount);
  const finalAmount = Math.max(0, amountBeforeDiscount - couponDiscount);

  const { setPaymentBreakdown, setSelectedCoupon: saveSelectedCoupon } = useBookingStore();

  // 선택된 쿠폰을 store에 저장
  useEffect(() => {
    if (selectedCoupon) {
      saveSelectedCoupon({
        user_coupon_id: selectedCoupon.user_coupon_id,
        coupon_id: selectedCoupon.coupon_id,
        name: selectedCoupon.name,
        discount_type: selectedCoupon.discount_type,
        discount_value: selectedCoupon.discount_value,
        discount_amount: couponDiscount,
      });
    } else {
      saveSelectedCoupon(null);
    }
  }, [selectedCoupon, couponDiscount, saveSelectedCoupon]);

  // 결제 세부 항목을 store에 저장
  useEffect(() => {
    setPaymentBreakdown({
      rental_fee: baseAmount,
      insurance_fee: insuranceFee,
      delivery_fee: deliveryFee,
      equipment_fee: totalEquipmentFee,
      discount: couponDiscount,
    });
  }, [baseAmount, insuranceFee, deliveryFee, totalEquipmentFee, couponDiscount, setPaymentBreakdown]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">{t('checkout.reviewReservation')}</h2>

      {/* Vehicle Info */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">{t('checkout.selectedVehicle')}</h3>
        <div className="flex gap-4">
          <img
            src={`/${vehicle.id}/01.jpg`}
            alt={getModelLabel(vehicle.model_name)}
            className="w-24 h-24 object-cover rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = vehicle.image_url || '/placeholder-car.svg';
            }}
          />
          <div>
            <p className="font-semibold text-lg">{getModelLabel(vehicle.model_name)}</p>
            <p className="text-sm text-gray-600">{vehicleMeta}</p>
          </div>
        </div>
      </div>

      {/* Rental Period */}
      <div className="mb-6 pb-6 border-b">
        <h3 className="text-lg font-semibold mb-3">{t('success.rentalPeriod')}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('success.rentalStart')}:</span>
            <span className="font-medium">{formatDateTime(startDateTime)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('success.returnDue')}:</span>
            <span className="font-medium">{formatDateTime(endDateTime)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('checkout.totalRentalTime')}:</span>
            <span className="font-medium">{formatDuration()}</span>
          </div>
        </div>
      </div>

      {(pickupLocationInfo?.address || returnLocationInfo?.address) && (
        <div className="mb-6 pb-6 border-b">
          <h3 className="text-lg font-semibold mb-3">{t('success.rentalOptions')}</h3>
          <div className="space-y-2 text-sm">
            {pickupLocationInfo?.address && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">{t('success.pickup')}:</span>
                <span className="font-medium text-right">{getLocationLabel(pickupLocationInfo.address)}</span>
              </div>
            )}
            {returnLocationInfo?.address && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">{t('success.return')}:</span>
                <span className="font-medium text-right">{getLocationLabel(returnLocationInfo.address)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="mb-6 pb-6 border-b">
        <h3 className="text-lg font-semibold mb-3">{t('success.customerInfo')}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('success.name')}:</span>
            <span className="font-medium">{userName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{t('success.phone')}:</span>
            <span className="font-medium">{userPhone}</span>
          </div>
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">{t('checkout.paymentAmount')}</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{t('success.rentalFee')}:</span>
            <span>{isLoadingPrice ? t('checkout.calculating') : formatPrice(baseAmount)}</span>
          </div>
          {insuranceFee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">{insuranceLabel}:</span>
              <span>{formatPrice(insuranceFee)}</span>
            </div>
          )}
          {equipmentFees.map(({ fee, label, equipment }) => (
            <div key={equipment.id} className="flex justify-between">
              <span className="text-gray-600">{label}:</span>
              <span>{formatPrice(fee)}</span>
            </div>
          ))}
          {deliveryFee > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t('success.deliveryFee')} ({isPickupDelivery && isReturnDelivery ? t('checkout.roundTrip') : t('checkout.oneWay')}):
              </span>
              <span>{formatPrice(deliveryFee)}</span>
            </div>
          )}

          {/* 쿠폰 선택 */}
          {showCouponSelector && isAuthenticated && (
            <div className="pt-3 border-t">
              <label htmlFor="coupon-select" className="block text-gray-700 font-semibold mb-2">
                {t('checkout.coupon')}
              </label>

              {isLoadingCoupons ? (
                <div className="text-sm text-gray-500">{t('checkout.loadingCoupons')}</div>
              ) : availableCoupons.length > 0 ? (
                <select
                  id="coupon-select"
                  value={selectedCoupon?.user_coupon_id || ''}
                  onChange={(e) => {
                    const couponId = e.target.value;
                    console.log('쿠폰 선택 시도:', couponId);
                    console.log('현재 주문 금액:', amountBeforeDiscount);

                    if (couponId === '') {
                      setSelectedCoupon(null);
                      setBookingCoupon(null);
                    } else {
                      const coupon = availableCoupons.find(c => c.user_coupon_id === Number(couponId));
                      if (coupon) {
                        console.log('선택한 쿠폰:', coupon);
                        console.log('쿠폰 최소 주문 금액:', coupon.min_order_amount);

                        // 최소 주문 금액 체크
                        const canUse = !coupon.min_order_amount || amountBeforeDiscount >= coupon.min_order_amount;
                        console.log('사용 가능 여부:', canUse);

                        if (canUse) {
                          setSelectedCoupon(coupon);
                          // 실제 할인 금액 계산
                          const actualDiscount = calculateCouponDiscount(coupon, amountBeforeDiscount);
                          console.log('계산된 할인 금액:', actualDiscount);
                          // bookingStore에 저장
                          setBookingCoupon({
                            user_coupon_id: coupon.user_coupon_id,
                            coupon_id: coupon.coupon_id,
                            name: coupon.name,
                            discount_type: coupon.discount_type,
                            discount_value: coupon.discount_value,
                            discount_amount: actualDiscount,
                          });
                        } else {
                          // 최소 주문 금액 미달 시 경고 메시지
                          alert(t('checkout.couponMinOrderAlert', { amount: formatPrice(coupon.min_order_amount || 0) }));
                          // 선택 해제
                          setSelectedCoupon(null);
                          setBookingCoupon(null);
                          // select 값도 초기화
                          e.target.value = '';
                        }
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">{t('checkout.selectCoupon')}</option>
                  {availableCoupons.map((coupon) => {
                    const canUse = !coupon.min_order_amount || amountBeforeDiscount >= coupon.min_order_amount;
                    // 표시용 할인 금액 (조건과 무관하게 계산)
                    const displayDiscount = calculateCouponDiscountForDisplay(coupon, amountBeforeDiscount);

                    return (
                      <option
                        key={coupon.user_coupon_id}
                        value={coupon.user_coupon_id}
                        disabled={!canUse}
                      >
                        {`${coupon.name} (-${formatPrice(displayDiscount)})${!canUse ? ` [${t('checkout.notApplicable')}]` : ''}${coupon.min_order_amount && !canUse ? ` (${t('checkout.minOrder', { amount: formatPrice(coupon.min_order_amount) })})` : ''}`}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="text-sm text-gray-500">{t('checkout.noCoupons')}</div>
              )}

              {/* 선택된 쿠폰 정보 표시 */}
              {selectedCoupon && (
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-sm font-semibold text-blue-900">{selectedCoupon.name}</div>
                  {selectedCoupon.description && (
                    <div className="text-xs text-blue-700 mt-1">{selectedCoupon.description}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 쿠폰 할인 */}
          {couponDiscount > 0 && (
            <div className="flex justify-between text-blue-600">
              <span>{t('success.couponDiscount')}:</span>
              <span>-{formatPrice(couponDiscount)}</span>
            </div>
          )}

          <div className="flex justify-between pt-3 border-t text-lg font-bold">
            <span>{t('success.totalPayment')}:</span>
            <span className="text-primary-600">{formatPrice(finalAmount)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
